/** Fetching the outlines the cards draw, a few at a time.
 *
 * `GET /v1/tracks` deliberately carries no geometry — a hundred circuits'
 * polylines is a megabyte a page does not need — so the map arrives as facts
 * and each card wears the design's placeholder loop. The real outline is
 * asked for afterwards, one circuit at a time, from the endpoint that serves
 * only the drawing, and a card becomes a real map when its answer lands.
 *
 * Only a published circuit can have an outline; asking for every row would be
 * a hundred `404`s for the ones surveyed but never published. And the
 * requests are bounded, a few in flight at once, because the point of the
 * split was that a reader on a slow connection pays for the list first and
 * the pictures second — not for the pictures all at once.
 *
 * A failed fetch is `null`, and a card with `null` keeps its placeholder: the
 * map is not broken because one drawing did not arrive.
 */

import type { Track, TrackGeometry } from '@/lib/types';

/** Only a circuit with a publication has anything to draw. */
export function wantsOutline(track: Pick<Track, 'last_published'>): boolean {
  return Boolean(track.last_published);
}

/**
 * Run `fetchOne` over every id with at most `concurrency` in flight, handing
 * each answer to `onEach` as it lands. Resolves when all have been tried; a
 * rejection becomes `null` rather than stopping the rest.
 */
export async function fetchOutlines(
  ids: readonly string[],
  fetchOne: (id: string) => Promise<TrackGeometry | null>,
  onEach: (id: string, outline: TrackGeometry | null) => void,
  concurrency = 4,
): Promise<void> {
  const queue = [...ids];
  const lanes = Math.max(1, Math.min(concurrency, queue.length));

  async function lane(): Promise<void> {
    for (let id = queue.shift(); id !== undefined; id = queue.shift()) {
      let outline: TrackGeometry | null = null;
      try {
        outline = await fetchOne(id);
      } catch {
        outline = null;
      }
      onEach(id, outline);
    }
  }

  await Promise.all(Array.from({ length: lanes }, () => lane()));
}
